import { visibleRepeatedAnswer } from "./repeatable";
import { assembleQuestionAnswer, embeddedFileRows } from "./groupAnswers";
import { z } from "zod";
import { isValidUrl } from "./url";
import {
  answerValueSchema,
  type AnswerMap,
  type AnswerValue,
  type FormContent,
  type Question,
  type ValidationRule,
} from "./types";
import { definitionFor, supportsValidator, takesAnswer } from "./registry";
import { isEmptyValue, visibleQuestions } from "./logic";
import { isCatastrophicPattern } from "./version";

/**
 * Whole-response validation — the one place the rules for "is this answer
 * acceptable" live.
 *
 * The browser runs `validateAnswers` per section as the applicant moves
 * through the form; the server will run `buildResponseSchema` over the whole
 * response on submit. Both call the same per-question check below, because two
 * implementations of the same rules is the real failure mode: the moment they
 * disagree, either the browser blocks an answer the server would accept, or
 * the server refuses one the browser said was fine — with the applicant stuck
 * between them.
 *
 * Only questions actually asked are validated. `visibleQuestions` walks the
 * jump path and evaluates conditions along it, so an answer to a question the
 * applicant was never shown can neither fail validation nor satisfy it.
 */

/** What's wrong with one question's answer, in words meant for the applicant. */
export type AnswerIssues = ReadonlyMap<string, readonly string[]>;

/**
 * Limits on saving answers, shared by the server route that enforces them
 * and the client that batches saves. One definition, so the client can keep
 * every batch it sends acceptable *by construction* — a server-side batch
 * refusal for size or count would strike every answer in the batch,
 * innocent ones included, and can't name which change offended.
 */

/** Per answer, as UTF-8 bytes of its JSON. A whole essay fits; a payload doesn't. */
export const MAX_ANSWER_JSON_BYTES = 100_000;
/** Changes per save request. */
export const MAX_SAVE_BATCH_CHANGES = 200;

const NO_ISSUES: readonly string[] = [];

/**
 * The problems with one answer, or none.
 *
 * `required` is checked here rather than by the answer schema because the
 * schema describes a *present* answer's shape — absence is a fact about the
 * response, not about the value.
 */
export function questionAnswerIssues(
  question: Question,
  value: AnswerValue | undefined,
): readonly string[] {
  if (!takesAnswer(question.type)) return NO_ISSUES;
  value = visibleRepeatedAnswer(question, value);

  if (value === undefined || isEmptyValue(value)) {
    return question.required ? ["This question is required."] : NO_ISSUES;
  }

  const issues: string[] = [];
  const definition = definitionFor(question.type);

  // Present is not the same as complete: a grid with rows still blank passes
  // the empty check (any answered row counts, so `isAnswered` conditions
  // behave) and the shape check (a partial value must be storable mid-fill).
  // Each type that can be part-answered says what required demands of it.
  if (question.required) {
    const incomplete = definition.requiredIssue?.(value, question);
    if (incomplete) issues.push(incomplete);
  }

  const parsed = definition.answerSchema(question).safeParse(value);
  if (!parsed.success) {
    // The schema's own messages are already written for the applicant
    // ("Choose one of the offered options"); deduplicated because a structural
    // failure often repeats per array element.
    for (const message of new Set(
      parsed.error.issues.map((issue) => issue.message),
    )) {
      issues.push(message);
    }
    // A value the schema refuses outright isn't worth running rules over —
    // the rules would be reading a shape they don't understand.
    return issues;
  }

  for (const rule of question.validation ?? []) {
    // A rule the type can't apply is skipped, not failed: the builder drops
    // them on a type change, but one added concurrently survives the merge,
    // and publishing is the gate that reports it. See `checkPublishable`.
    if (!supportsValidator(question.type, rule.kind)) continue;
    const issue = ruleIssue(rule, value);
    if (issue) issues.push(issue);
  }
  return issues;
}

/** The text a text-shaped rule reads, or null when the value has no text. */
function textOf(value: AnswerValue): string | null {
  return value.kind === "text" ? value.value : null;
}

/** How many selections a selection-counting rule sees. */
function selectedCount(value: AnswerValue): number | null {
  if (value.kind !== "choices") return null;
  // A blank "other" box is not a tick — same rule as `isEmptyValue`.
  return value.choices.filter(
    (choice) => choice.kind !== "other" || choice.value.trim() !== "",
  ).length;
}

function ruleIssue(rule: ValidationRule, value: AnswerValue): string | null {
  switch (rule.kind) {
    case "minLength": {
      const text = textOf(value);
      return text !== null && text.length < rule.value
        ? `Use at least ${rule.value} character${rule.value === 1 ? "" : "s"}.`
        : null;
    }
    case "maxLength": {
      const text = textOf(value);
      return text !== null && text.length > rule.value
        ? `Use at most ${rule.value} character${rule.value === 1 ? "" : "s"}.`
        : null;
    }
    case "email": {
      const text = textOf(value);
      return text !== null && !z.email().safeParse(text.trim()).success
        ? "Enter a valid email address."
        : null;
    }
    case "url": {
      const text = textOf(value);
      return text !== null && !isValidUrl(text) ? "Enter a valid link." : null;
    }
    case "regex": {
      const text = textOf(value);
      if (text === null) return null;
      // A catastrophic pattern checks nothing, same as one that doesn't
      // compile: publishing refuses both, but a draft being previewed hasn't
      // been published yet, and running one here would freeze the tab — or,
      // on submit, the server.
      if (isCatastrophicPattern(rule.pattern)) return null;
      let matched: boolean;
      try {
        matched = new RegExp(rule.pattern).test(text);
      } catch {
        return null;
      }
      if (rule.negate === true) {
        return matched ? "This answer isn't allowed here." : null;
      }
      return matched ? null : "This answer doesn't match the required format.";
    }
    case "contains": {
      const text = textOf(value);
      if (text === null) return null;
      const has = text.includes(rule.value);
      if (rule.negate === true) {
        return has ? `Must not contain “${rule.value}”.` : null;
      }
      return has ? null : `Must contain “${rule.value}”.`;
    }
    case "number": {
      const text = textOf(value);
      return text !== null && !isNumeric(text) ? "Enter a number." : null;
    }
    case "integer": {
      const text = textOf(value);
      if (text === null) return null;
      if (!isNumeric(text)) return "Enter a whole number.";
      return Number.isInteger(Number(text.trim()))
        ? null
        : "Enter a whole number.";
    }
    case "min": {
      const number = numericOf(value);
      if (number === null)
        return `Enter a number that is at least ${rule.value}.`;
      return number < rule.value
        ? `Enter a number that is at least ${rule.value}.`
        : null;
    }
    case "max": {
      const number = numericOf(value);
      if (number === null)
        return `Enter a number that is at most ${rule.value}.`;
      return number > rule.value
        ? `Enter a number that is at most ${rule.value}.`
        : null;
    }
    case "minSelected": {
      const count = selectedCount(value);
      return count !== null && count < rule.value
        ? `Choose at least ${rule.value} option${rule.value === 1 ? "" : "s"}.`
        : null;
    }
    case "maxSelected": {
      const count = selectedCount(value);
      return count !== null && count > rule.value
        ? `Choose at most ${rule.value} option${rule.value === 1 ? "" : "s"}.`
        : null;
    }
  }
}

/** A text answer that reads as one number, in full — "12abc" is not 12. */
function isNumeric(text: string): boolean {
  const trimmed = text.trim();
  return trimmed !== "" && Number.isFinite(Number(trimmed));
}

/**
 * The number a numeric bound compares against.
 *
 * `min`/`max` are offered on text questions (via the `number` rule family),
 * whose answers are text; a number-shaped answer would satisfy them directly
 * if a type ever offered both.
 */
function numericOf(value: AnswerValue): number | null {
  if (value.kind === "number") return value.value;
  const text = textOf(value);
  if (text === null || !isNumeric(text)) return null;
  return Number(text.trim());
}

/**
 * Every problem with the response so far, keyed by question id.
 *
 * Questions the walk never reaches aren't validated — their answers are
 * leftovers the applicant walked back, and submit discards rather than judges
 * them. Restrict to one section by filtering the result against that
 * section's questions; the *walk* still runs over the whole form, because a
 * question's visibility can hang on any earlier answer.
 */
export function validateAnswers(
  form: FormContent,
  answers: AnswerMap,
): AnswerIssues {
  const issues = new Map<string, readonly string[]>();
  for (const question of visibleQuestions(form, answers)) {
    const found = questionAnswerIssues(
      question,
      assembleQuestionAnswer(question, answers[question.id], answers),
    );
    if (found.length > 0) issues.set(question.id, found);
  }
  return issues;
}

/**
 * The whole response as one schema — what the server checks on submit.
 *
 * A zod wrapper over `validateAnswers`, not a second rule set: each problem
 * becomes an issue at path `[questionId]`, so a caller can hand errors back
 * per question.
 *
 * What parses out is the response as it should be *stored*, not the map as it
 * arrived: answers to questions the walk never asked are discarded, so a
 * reviewer never sees an answer the applicant walked back — the same decision
 * validation already makes by not judging them. Validate-but-return-unchanged
 * would quietly hand the server the one thing it must not keep, malformed
 * leftovers included. What *is* kept is each answer as its own schema parses
 * it — trimmed, stripped, normalized — never the raw input.
 *
 * This is the untrusted entry point, so nothing downstream of it may touch a
 * value that isn't answer-shaped at all: `isEmptyValue` and the walk both
 * dereference into composites, and `{ kind: "grid" }` with no rows must come
 * back as a zod issue, not a TypeError. Unreadable values are split out first
 * and reported; the walk and validation run over what remains.
 */
export function buildResponseSchema(form: FormContent): z.ZodType<AnswerMap> {
  return z
    .custom<AnswerMap>(
      // An array is `typeof "object"` too, and an empty one would sail
      // through an optional-only form as a "successful" empty response.
      (candidate) =>
        typeof candidate === "object" &&
        candidate !== null &&
        !Array.isArray(candidate),
      { message: "Answers must be an object keyed by question id." },
    )
    .superRefine((answers, ctx) => {
      const { readable, unreadableIds } = splitByShape(answers);
      // Unreadable values are reported only where the question was actually
      // asked — a malformed leftover under a hidden or deleted question gets
      // discarded exactly like a well-formed one, not turned into a rejection
      // no answer the applicant can see would explain. The walk runs over the
      // readable map, so an unreadable value steers nothing either way.
      const asked = new Set(
        visibleQuestions(form, readable).map((question) => question.id),
      );
      for (const questionId of unreadableIds) {
        if (!asked.has(questionId)) continue;
        ctx.addIssue({
          code: "custom",
          message: "This answer isn't in a recognisable shape.",
          path: [questionId],
        });
      }
      for (const [questionId, messages] of validateAnswers(form, readable)) {
        for (const message of messages) {
          ctx.addIssue({
            code: "custom",
            message,
            path: [questionId],
          });
        }
      }
    })
    .transform((answers) => {
      // Only reached with zero issues, so every remaining value has already
      // passed its question's schema — the split is belt and braces.
      const { readable } = splitByShape(answers);
      const kept: AnswerMap = {};
      const visible = visibleResponseAnswers(form, readable);
      for (const question of visibleQuestions(form, readable)) {
        const value = visible[question.id];
        if (value === undefined) continue;
        // Store what the answer's own schema makes of it — an Other of
        // "  custom  " is promised trimmed — falling back to the raw value
        // only if the parse somehow diverges from validation above.
        const parsed = definitionFor(question.type)
          .answerSchema(question)
          .safeParse(value);
        kept[question.id] = parsed.success ? parsed.data : value;
        Object.assign(kept, embeddedFileRows(question.id, kept[question.id]!));
      }
      return kept;
    });
}

/** Keep the current visible path even while required answers are incomplete. */
export function visibleResponseAnswers(
  form: FormContent,
  answers: AnswerMap,
): AnswerMap {
  const kept: AnswerMap = {};
  for (const question of visibleQuestions(form, answers)) {
    if (!takesAnswer(question.type)) continue;
    const value = visibleRepeatedAnswer(
      question,
      assembleQuestionAnswer(question, answers[question.id], answers),
    );
    if (value === undefined || isEmptyValue(value)) continue;
    kept[question.id] = value;
    Object.assign(kept, embeddedFileRows(question.id, value));
  }
  return kept;
}

/**
 * The entries whose values are answer-shaped at all, and the ids of those
 * that aren't. Shape here is the `AnswerValue` union — per-question fit is
 * validation's job; this only guarantees the code that walks and inspects
 * values can't be handed something it would throw on.
 */
function splitByShape(answers: AnswerMap): {
  readable: AnswerMap;
  unreadableIds: string[];
} {
  const readable: AnswerMap = {};
  const unreadableIds: string[] = [];
  for (const [questionId, value] of Object.entries(answers)) {
    if (value === undefined) continue;
    if (answerValueSchema.safeParse(value).success) {
      readable[questionId] = value;
    } else {
      unreadableIds.push(questionId);
    }
  }
  return { readable, unreadableIds };
}
